/** Remediation requests list — active vs cancelled row styling. */

import {
  getStatusMeta,
  getStatusRowGroup,
  isInactiveStatus,
  STATUS_ROW_GROUP,
} from "./statusColors";

export const formatRequestRef = (id) =>
  id != null && id !== "" ? `T-${id}` : "—";

const isEmptySortValue = (value) =>
  value == null || value === "" || (typeof value === "number" && Number.isNaN(value));

const compareSortValues = (a, b, dir) => {
  const aEmpty = isEmptySortValue(a);
  const bEmpty = isEmptySortValue(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (typeof a === "number" && typeof b === "number") {
    return dir === "desc" ? b - a : a - b;
  }
  const cmp = String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return dir === "desc" ? -cmp : cmp;
};

const toSortTime = (value) => {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
};

const columnSortValue = (row, field) => {
  switch (field) {
    case "id": {
      const n = Number(row?.id);
      return Number.isFinite(n) ? n : null;
    }
    case "resource_arn":
      return row?.resource_arn || "";
    case "account":
      return `${row?.account_name || ""}\u0000${row?.account_number || ""}`;
    case "status":
      return `${getStatusMeta(row?.status).label}\u0000${row?.status || ""}`;
    case "scheduled_at":
      return toSortTime(row?.scheduled_at);
    case "created_by":
      return row?.created_by || row?.user_email || "";
    default:
      return null;
  }
};

/** Client-side sort of the current page. Does not mutate `items`. */
export const sortRequestsByColumn = (items, field, dir) => {
  if (!Array.isArray(items)) return [];
  const direction = dir === "desc" ? "desc" : "asc";
  return [...items].sort((a, b) =>
    compareSortValues(columnSortValue(a, field), columnSortValue(b, field), direction)
  );
};

const INACTIVE_ROW = {
  rowBg: "#e2e8f0",
  hoverBg: "#cbd5e1",
  textColor: "#64748b",
  opacity: 0.88,
};

const GROUP_ROW_DEFAULTS = {
  [STATUS_ROW_GROUP.ACTIVE]: {
    hoverBg: "#f1f5f9",
  },
  [STATUS_ROW_GROUP.ATTENTION]: {
    hoverBg: null,
  },
  [STATUS_ROW_GROUP.SUCCESS]: {
    hoverBg: "#dcfce7",
  },
  [STATUS_ROW_GROUP.FAILURE]: {
    hoverBg: "#fce7f3",
  },
  [STATUS_ROW_GROUP.INACTIVE]: INACTIVE_ROW,
};

export const getRequestRowSx = (request) => {
  const status = request?.status;
  const meta = getStatusMeta(status);
  const group = getStatusRowGroup(status);
  const inactive = isInactiveStatus(status);
  const groupDefaults = GROUP_ROW_DEFAULTS[group] || GROUP_ROW_DEFAULTS[STATUS_ROW_GROUP.ACTIVE];

  const rowBg = inactive ? INACTIVE_ROW.rowBg : meta.rowBg || "#ffffff";
  const hoverBg = inactive
    ? INACTIVE_ROW.hoverBg
    : groupDefaults.hoverBg || meta.bg || "#f8fafc";

  return {
    cursor: "pointer",
    backgroundColor: rowBg,
    ...(inactive
      ? {
          opacity: INACTIVE_ROW.opacity,
          "& .MuiTableCell-root": { color: INACTIVE_ROW.textColor },
          "& .MuiTypography-root": { color: "inherit" },
          "& .MuiCheckbox-root": { color: "#94a3b8" },
        }
      : {}),
    "&:hover": {
      backgroundColor: hoverBg,
      ...(inactive ? { opacity: 1 } : {}),
    },
  };
};
